/**
 * Superuser scope tokens for the command palette.
 *
 * Ops power users can constrain the universal `search` to a single entity type
 * by typing a `t:<scope>` token and pressing Tab. The token is lifted out of
 * the text field into an inline "pill" (see CommandPalette), and its mapped
 * SearchType is passed to the graph `search(searchTypes: [...])` argument.
 *
 * Scoping does two things:
 *   1. Narrows the fan-out to one entity type server-side.
 *   2. Forces the server's *adaptive result budget* into single-category mode,
 *      so the user gets a deep list (~10) of exactly the thing they asked for
 *      instead of shallow slices across categories.
 *
 * This module is pure — no React, no network. It owns the token vocabulary and
 * the parsing/completion rules so the component stays declarative.
 */

/** The graph `SearchType` enum values we can scope to. */
export type SearchType =
  | 'LISTINGS'
  | 'ACTIVE_LISTINGS'
  | 'PROPERTY'
  | 'VENUE'
  | 'EVENT'
  | 'TRANSACTION'
  | 'ORGANIZATION'
  | 'USER'

export interface ScopeToken {
  /** The token the user types after `t:` (e.g. 'la'). */
  readonly token: string
  /** Graph SearchType this maps to. */
  readonly type: SearchType
  /** Short label rendered inside the pill. */
  readonly label: string
}

/**
 * Token vocabulary. Order matters for completion: longer, more specific tokens
 * are listed near their prefixes so `t:la` resolves to Live Auctions, not a
 * partial of `t:l`.
 *
 * Listings come in two scopes: `t:l` (all listings) and `t:al` (active only).
 * Public surfaces search active listings; internal ops needs both. The server
 * treats LISTINGS as a superset of ACTIVE_LISTINGS, so sending both collapses to
 * LISTINGS. Past listings for a property are also reachable via the property view.
 */
export const SCOPE_TOKENS: ReadonlyArray<ScopeToken> = [
  { token: 'l', type: 'LISTINGS', label: 'Listings' },
  { token: 'la', type: 'VENUE', label: 'Live Auctions' },
  { token: 'al', type: 'ACTIVE_LISTINGS', label: 'Active Listings' },
  { token: 'p', type: 'PROPERTY', label: 'Properties' },
  { token: 'e', type: 'EVENT', label: 'Events' },
  { token: 'tx', type: 'TRANSACTION', label: 'Transactions' },
  { token: 'o', type: 'ORGANIZATION', label: 'Organizations' },
  { token: 'u', type: 'USER', label: 'Users' },
]

const PREFIX = 't:'

/**
 * Given the current input value, return the scope token being typed (the text
 * after `t:` at the *start* of the input), or null if the input isn't a scope
 * token expression.
 *
 * We only treat a leading `t:` as a scope token so ordinary queries that happen
 * to contain a colon (addresses, urns) are never hijacked.
 */
export function pendingScopeFragment(value: string): string | null {
  if (!value.startsWith(PREFIX)) return null
  return value.slice(PREFIX.length)
}

/**
 * Resolve a fragment to the token(s) it could complete to.
 *   - exact match -> [that token]
 *   - unique prefix -> [the single matching token]
 *   - ambiguous prefix -> [all matches] (caller shows a completion menu)
 *   - no match -> []
 */
export function completionsFor(fragment: string): ReadonlyArray<ScopeToken> {
  const f = fragment.trim().toLowerCase()
  if (!f) return SCOPE_TOKENS
  // Return every prefix match (including an exact one) so the hint menu can
  // show, e.g., both `t:l` and `t:la` while the user is still typing. Exact
  // disambiguation for Tab lives in `resolveTabCompletion`.
  return SCOPE_TOKENS.filter(t => t.token.startsWith(f))
}

/**
 * Resolve the token to lock when Tab is pressed on the given fragment.
 * Returns the token when there's an unambiguous choice (exact or single
 * prefix match), else null (ambiguous / no match — caller keeps typing).
 */
export function resolveTabCompletion(fragment: string): ScopeToken | null {
  const matches = completionsFor(fragment)
  if (matches.length === 1) return matches[0]
  // Exact match still wins even when it's also a prefix of a longer token.
  const exact = SCOPE_TOKENS.find(
    t => t.token === fragment.trim().toLowerCase()
  )
  return exact ?? null
}

/** Map locked scopes to the `searchTypes` argument for the graph `search` query. */
export function toSearchTypes(
  scopes: ReadonlyArray<ScopeToken>
): SearchType[] | undefined {
  if (!scopes.length) return undefined
  // De-dupe while preserving order.
  return Array.from(new Set(scopes.map(s => s.type)))
}
